import { z } from 'zod';
import { objectId, timestampFields, FieldMetadata } from './base';

/**
 * AccessRequest Schema (INT-0032)
 *
 * A signed-in mobile user's request to join a DivisionTeam's PRIVATE space
 * (chat / RSVP / photos / assignments — everything getTeamAccess gates).
 * Public data (schedules/standings) never needs one: following an
 * organization is unauthenticated-public by design (GameChanger model,
 * owner decision 2026-10-02). The zero-friction path remains the roster
 * email auto-link (linkOrphanPlayers); this is the fallback for guardians/
 * players whose email isn't on the roster.
 *
 * Lifecycle: pending → approved | denied, decided by a team manager
 * (registrant or teamManagers email, or an org-chain admin) either in-app
 * or via emailed single-use magic links. Approval materializes NOTHING —
 * getTeamAccess widens isMember with an approved-request lookup, so denying
 * later is as simple as deleting the request. A withdrawn (soft-deleted) or
 * denied request does not block re-requesting; one PENDING request per
 * (user, divisionTeam) is enforced by a partial unique index in the model.
 */
export const AccessRequestSchema = z.object({
  _id: objectId,
  user: objectId,
  divisionTeam: objectId,
  // Denormalized for list screens + scoping; stamped server-side at create.
  organization: objectId.optional(),
  role: z.enum(['player', 'guardian']),
  status: z.enum(['pending', 'approved', 'denied']).default('pending'),
  message: z.string().max(500).optional(),
  // Decision audit: in-app decisions stamp decidedByUser; magic-link
  // decisions stamp decidedByEmail (the token's recipient).
  decidedByUser: objectId.optional(),
  decidedByEmail: z.string().optional(),
  decidedAt: z.coerce.date().optional(),
  // One emailed magic link per manager recipient: sha256 hash at rest, raw
  // token only ever inside the emailed URL (admin reset-token pattern).
  decisionTokens: z.array(z.object({
    emailLc: z.string(),
    tokenHash: z.string(),
  })).default([]),
  tokenExpires: z.coerce.date().optional(),
  ...timestampFields,
});

export type AccessRequest = z.infer<typeof AccessRequestSchema>;

export const accessRequestMetadata: Record<string, FieldMetadata> = {
  _id: { hidden: true },
  user: { ref: 'User', label: 'Requester' },
  divisionTeam: { ref: 'DivisionTeam', label: 'Team' },
  organization: { ref: 'Organization', label: 'Organization' },
  role: { label: 'Role' },
  status: { label: 'Status' },
  message: { label: 'Message' },
  decidedByUser: { ref: 'User', label: 'Decided By (User)' },
  decidedByEmail: { label: 'Decided By (Email)' },
  decidedAt: { label: 'Decided At' },
  decisionTokens: { hidden: true },
  tokenExpires: { hidden: true },
  created: { hidden: true },
  updated: { hidden: true },
};
